// PartLabelTemplate.tsx
import React from "react";
import type { PartLabelDisplay } from "../../models/LabelDisplay";
import useLabels from "../../data/useLabels";

export type PartLabelTemplateProps = {
  display: PartLabelDisplay;

  // values
  date?: string;
  tagNo?: string;
  logoSrc?: string;

  make?: string;
  model?: string;
  category?: string;

  qrSrc?: string;
  price?: string;

  vrm?: string;
  year?: string;
  engineCode?: string;
  vin?: string;
  fuelType?: string;

  position?: string;
  qc?: string;
  imaged?: string;
};

const textOrNull = (enabled: boolean, value?: string) => {
  const v = (value ?? "").trim();
  return enabled && v ? v : null;
};

export default function PartLabelTemplate(props: PartLabelTemplateProps) {
  const { display } = props;

  const vehicleLines = [
    textOrNull(display.vehicleStack.make      , props.make),
    textOrNull(display.vehicleStack.model     , props.model),
    textOrNull(display.vehicleStack.vrm       , props.vrm),
    textOrNull(display.vehicleStack.year      , props.year),
    textOrNull(display.vehicleStack.engineCode, props.engineCode),
    textOrNull(display.vehicleStack.vin       , props.vin),
    textOrNull(display.vehicleStack.fuelType  , props.fuelType)
  ].filter(Boolean) as string[];

  return (
    <html>
      <head>
        <meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=1,minimum-scale=1,user-scalable=no" />
      </head>

      <body style={{ margin: 0, padding: 0 }}>
        <div
          style={{
            width: "10cm",
            height: "5cm",
            display: "grid",
            gridTemplateColumns: "9cm 1cm",
            gridTemplateRows: "1cm 1cm 3cm",
            fontFamily: "Arial,Helvetica,sans-serif",
            boxSizing: "border-box",
            overflow: "hidden"
          }}
        >
          {/* Right vertical date strip (optional) */}
          {display.date && (
            <div
              style={{
                gridColumn: 2 as any,
                gridRow: "1/span 3",
                writingMode: "vertical-rl",
                textOrientation: "mixed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                transform: "rotate(180deg)"
              }}
            >
              {props.date ?? ""}
            </div>
          )}

          {/* Header row */}
          <div style={{ gridColumn: 1 as any, gridRow: 1 as any, display: "grid", gridTemplateColumns: "4cm 5cm" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "start",
                fontWeight: 700,
                fontSize: "16px",
                height: "1cm"
              }}
            >
              {display.tagNo ? (props.tagNo ?? "") : null}
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "start" }}>
              {display.logo && props.logoSrc ? (
                <img alt="Logo" src={props.logoSrc} style={{ height: "1cm" }} />
              ) : null}
            </div>
          </div>

          {/* Make/Model/Category line */}
          <div
            style={{
              gridColumn: 1 as any,
              gridRow: 2 as any,
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              fontWeight: 700,
              height: "1cm",
              lineHeight: 1
            }}
          >
            {display.makeModelCategoryLine
              ? [props.make, props.model].filter(Boolean).join(" ") +
                (props.category ? ` • ${props.category}` : "")
              : null}
          </div>

          {/* Main row */}
          <div
            style={{
              gridColumn: 1 as any,
              gridRow: 3 as any,
              display: "grid",
              gridTemplateColumns: "2cm 4cm 3cm"
            }}
          >
            {/* Left: QR + price */}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div
                style={{
                  flex: "1 1 auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "2cm",
                  width: "2cm"
                }}
              >
                {display.qr && props.qrSrc ? (
                  <img alt="QR" src={props.qrSrc} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                ) : null}
              </div>

              <div style={{ fontWeight: 700, display: "flex", justifyContent: "start", alignItems: "end", height: "1cm" }}>
                {display.price ? (props.price ?? "") : null}
              </div>
            </div>

            {/* Middle: stacked vehicle details (NO whitespace because we filter lines) */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "end",
                gap: "2px",
                textAlign: "center",
                fontSize: "12px",
                lineHeight: 1.2,
                height: "3cm"
              }}
            >
              {vehicleLines.map((line, idx) => (
                <div key={idx}>{line}</div>
              ))}
            </div>

            {/* Right: position/QC/img */}
            <div
              style={{
                display: "grid",
                gridTemplateRows: "1.5cm 1.5cm",
                gridTemplateColumns: "1.5cm 1.5cm",
                height: "100%"
              }}
            >
              <div
                style={{
                  gridColumn: "1/span 2",
                  display: "flex",
                  alignItems: "end",
                  justifyContent: "center",
                  fontWeight: 700,
                  textAlign: "center"
                }}
              >
                {display.position ? (props.position ?? "-") : null}
              </div>

              <div style={{ display: "flex", alignItems: "end", justifyContent: "center", textAlign: "center" }}>
                {display.qc ? (props.qc ?? "-") : null}
              </div>

              <div style={{ display: "flex", alignItems: "end", justifyContent: "center", textAlign: "center" }}>
                {display.imaged ? (props.imaged ?? "-") : null}
              </div>
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}
